import React, {Fragment, useEffect, useLayoutEffect, useState} from "react";
import {resendVerification, signIn} from "@simplyhealth/iam_client_lib";
import signInImage from "../../assets/images/simplyhealth_logo.png";
import {Button} from "../../parts/button/Button";
import {FormContainer} from "../../parts/form-container/FormContainer";
import {Progress} from "../../parts/progress/Progress";
import {ErrorMessages} from "../../parts/error-messages/ErrorMessages";
import {FormFooter} from "../../parts/form-footer/FormFooter";
import {createInitialLoginPageModel, LoginPageModel, LoginPageStatus} from "./helpers/login-page-model";
import {LoginFieldSet} from "./LoginFieldSet";
import {areAllValid, createInitialFieldModel, FieldModel} from "../../shared/model/field-model";
import {createEmailValidator, createRequiredValidator} from "../../helpers/create-validators";
import {createFieldHandlers} from "../../helpers/create-field-handlers";
import {createElementName} from "../../shared/helpers/element.naming";
import {
  shouldShowResendVerification,
} from "./helpers/login-response-code-helpers";
import {updatePageModelForStatusChangeReducer, updatePageModelReducer} from "./helpers/login-page-model-reducers";
import {createResendVerificationRequest} from "../../helpers/request-helpers";
import {createSignInRequest} from "./helpers/login-request-helpers";
import {fromQueryParameters} from "../../helpers/query-parameters-helper";
import {isCompleted, isErrored} from "../../helpers/display-block-logic";
import {createRedirectUrl} from "../../shared/helpers/login-redirect-helper";
import {ANALYTICS_NAMES, sendPageVisitAnalytics} from "../../helpers/analytics-helpers";
import {useHistory} from "react-router-dom";
import jwtDecode from 'jwt-decode'
import {createSignInUserSession, saveSignInUserStorage} from "../../shared/helpers/login-session-helpers";
import {getRedirectUrl} from "../../shared/helpers/appclient-helper";
import {CodeDeliveryDto, createCodeDeliveryDto} from "../../shared/model/user-model";
import {Redirect} from "react-router";
import {isProcessing, isRedirect} from "./helpers/login-page-status-helper";
import { createUIMessage } from "../../helpers/response-error-message-handler"

export const LogIn = () => {
  const PAGE_NAME = "login";

  const [pageModel, setPageModel] = useState<LoginPageModel>(createInitialLoginPageModel());
  const [pageStatus, setPageStatus] = useState<LoginPageStatus>("Ready");
  const [accessToken, setAccessToken] = useState("");
  const [refreshToken, setRefreshToken] = useState("");
  const [idToken, setIdToken] = useState("");
  const [redirectUrlState, setRedirectUrlState] = useState<string>("");

  const [codeDeliveryDto, setCodeDeliveryDto] = useState<CodeDeliveryDto>();

  const [emailModel, setEmailModel] = useState<FieldModel>(createInitialFieldModel());
  const [passwordModel, setPasswordModel] = useState<FieldModel>(createInitialFieldModel());

  const [onEmailChange, onEmailBlur, invokeEmailValidation] = createFieldHandlers(
    emailModel,
    setEmailModel,
    createEmailValidator()
  );
  const [onPasswordChange, onPasswordBlur, invokePasswordValidation] = createFieldHandlers(
    passwordModel,
    setPasswordModel,
    createRequiredValidator("Password")
  );

  const history = useHistory();

  useLayoutEffect(() => {
    if (!fromQueryParameters().doAllLoginExist()) {
      setPageStatus(() => "Missing Query Parameters");
    }
    try{
      if (fromQueryParameters().getRedirectUri()) {
        setRedirectUrlState(getRedirectUrl());
      } else {
        setRedirectUrlState("");
      }
    }catch (error) {
      setPageStatus(()=>"Missing Query Parameters");
    }
  }, []);

  useEffect(() => {
    document.title = "Account login | Simplyhealth";
  }, []);

  useEffect(() => {
    sendPageVisitAnalytics(ANALYTICS_NAMES.login);
  }, []);

  useEffect(() => {
    if (pageStatus === "Redirect") {
      if (redirectUrlState) {
        redirectToUri();
      } else {
        history.push("/registrationcomplete");
      }

    } else if (pageStatus === "CompleteRegistration") {
      let userSession = createSignInUserSession(emailModel.value, accessToken, refreshToken, idToken, redirectUrlState, fromQueryParameters().getState());
      saveSignInUserStorage(userSession);
      history.push("/completeregistration")
    } else if (pageStatus === "Ready") {
      let title = "Login";
      let informationMessages = [
        "Please note that if you usually log in with a username, you'll now need to use your email address to access your account.",
        "To access Simplyhealth services, please enter your Email Address and Password."
      ];
      if(!fromQueryParameters().getRedirectUri()){
        title = "Complete registration";
        informationMessages = ["Please log in to complete your Simplyhealth account registration"];
      }
      setPageModel((pm) => updatePageModelForStatusChangeReducer({...pm, title, informationMessages}, pageStatus));
    } else {
      setPageModel((pm) => updatePageModelForStatusChangeReducer(pm, pageStatus));
    }
  }, [pageStatus]);

  const redirectToUri = () => {
    window.location.href = createRedirectUrl(
        redirectUrlState,
        accessToken,
        refreshToken,
        idToken,
        fromQueryParameters().getState()
    );
  };

  const onSubmitClick = () => {
    switch (pageStatus) {
      case "Ready":
        sendSignInRequest();
        break;
      case "Sending":
        // Do nothing
        break;
      case "Succeeded":
        if (pageModel.subStatus === "Resend Verification Succeeded") {
          setPageStatus("Ready");
        }
        break;
      case "Failed":
        setPageStatus("Ready");
        break;
    }
  };

  const sendSignInRequest = () => {
    invokeEmailValidation();
    invokePasswordValidation();
    if (areAllValid(emailModel, passwordModel)) {
      setPageStatus("Sending");
      const request = createSignInRequest(
          emailModel.value,
          passwordModel.value,
          redirectUrlState,
      );
      signIn(request)
        .then((response) => {
          if (response.status === "succeeded") {
            setAccessToken(response.accessToken);
            setRefreshToken(response.refreshToken);
            setIdToken(response.idToken);


            const profile: any = jwtDecode(response.idToken)

            if (profile["custom:cashplan_member_id"] !== undefined) {
              setPageStatus("Redirect");
            } else {
              setPageStatus("CompleteRegistration");
            }
          } else {
            const [informationMessages, title] = prepareErrorMsg(response?.message);
            const showResendVerificationButton = shouldShowResendVerification(response.code);
            setPageModel(
              updatePageModelReducer(pageModel, { informationMessages, title, showResendVerificationButton })
            );
            setPageStatus("Failed");
          }
        })
        .catch(() => {
          const [informationMessages, title] = prepareErrorMsg("");
          setPageModel(
            updatePageModelReducer(pageModel, { informationMessages, title, showResendVerificationButton: false })
          );
          setPageStatus("Failed");
        });
    }
  };

  const prepareErrorMsg = (message: string):  [string[], string] => {
    return createUIMessage(message, "log you in");
  };

  const onResendVerificationClick = () => {
    sendResendVerificationRequest();
  };

  const sendResendVerificationRequest = () => {
    setPageStatus("Sending");
    const request = createResendVerificationRequest(emailModel.value);
    resendVerification(request)
      .then((response) => {
        if (response.status === "succeeded") {

          setCodeDeliveryDto(createCodeDeliveryDto(response.codeDeliveryMethod, response.codeDeliveryDestination,emailModel.value.toLowerCase()));
          /*const informationMessages = createResendSuccessMessages(response.codeDeliveryDestination);
          setPageModel(
            updatePageModelReducer(pageModel, { informationMessages, subStatus: "Resend Verification Succeeded" })
          );*/
          setPageStatus("Resend Verification Succeeded");
        } else {
          const [informationMessages, title] = prepareErrorMsg(response?.message);
          const showResendVerificationButton = shouldShowResendVerification(response.code);
          setPageModel(updatePageModelReducer(pageModel, { informationMessages, title, showResendVerificationButton }));
        }
      })
      .catch(() => {
        const [informationMessages, title] = prepareErrorMsg("");
        setPageModel(
          updatePageModelReducer(pageModel, { informationMessages, title, showResendVerificationButton: false })
        );
        setPageStatus("Failed");
      });
  };

  return (
    <FormContainer
      pageName={PAGE_NAME}
      title={pageModel.title}
      messages={pageModel.informationMessages}
      image={<img src={signInImage} alt="" />}
      fields={
        isProcessing(pageStatus) || isRedirect(pageStatus) ? (
            <Fragment>
              <Progress/>
            </Fragment>
        ) : (pageStatus === "Resend Verification Succeeded") ? (
            <Redirect to={{
              pathname: "/verification",
              state: codeDeliveryDto
            }}/>
        ) : isCompleted(pageStatus) ? (
          <Fragment />
        ) : isErrored(pageStatus) ? (
          <Fragment>
            <ErrorMessages
              name={createElementName(PAGE_NAME, "submit_errors", "errors")}
              messages={pageModel.errorMessages}
            />
            {pageModel.showResendVerificationButton && (
              <Button
                pageName={PAGE_NAME}
                name="resendverification"
                caption="Resend Verification Email"
                onClick={onResendVerificationClick}
              />
            )}
          </Fragment>
        ) : (
          <LoginFieldSet
            pageName={PAGE_NAME}
            emailState={emailModel}
            onEmailChange={onEmailChange}
            onEmailBlur={onEmailBlur}
            passwordState={passwordModel}
            onPasswordChange={onPasswordChange}
            onPasswordBlur={onPasswordBlur}
          />
        )
      }
      action={
        pageModel.showButton && (
          <Button pageName={PAGE_NAME} name="submit" caption={pageModel.buttonTitle} onClick={onSubmitClick} />
        )
      }
      footer={
        pageModel.showFooter && <FormFooter message="New to Simplyhealth?" linkText="Register" redirectTo="/register" />
      }
    />
  );
};
